import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📘',
  number: 'Tomo 1',
  title: 'Las Bases del Taller Profesional',
  subtitle: 'La inocuidad y los fundamentos técnicos que tus alumnos necesitan entender antes de tocar un ingrediente.',
  problem: 'El primer contacto con el procesamiento de alimentos puede ser el más transformador o el más peligroso. Este tomo convierte los principios de inocuidad y calidad en algo comprensible y aplicable desde el primer día de práctica, sin importar en qué país de América Latina estés enseñando.',
  bullets: ['De la materia prima al producto final: el recorrido técnico completo', 'Higiene, inocuidad y Buenas Prácticas de Manufactura (BPM)', 'Los tres parámetros que todo alumno debe conocer: temperatura, pH y actividad de agua', 'Limpieza y desinfección correcta del taller escolar', 'Normas de seguridad específicas para el procesamiento artesanal'],
  result: 'Tus alumnos entienden el procesamiento de alimentos como un proceso técnico y seguro, no como una receta de cocina improvisada.'
}, {
  icon: '📗',
  number: 'Tomo 2',
  title: 'De la Materia Prima a la Conservación',
  subtitle: 'El núcleo técnico que explica por qué los alimentos se conservan y cuándo son realmente seguros para el consumo.',
  problem: 'Este es uno de los puntos donde más errores se cometen y donde la falta de conocimiento técnico puede tener consecuencias reales de inocuidad. Este tomo te da el material listo para que tus alumnos comprendan la seguridad alimentaria con fundamento científico y ejemplos de su propia región.',
  bullets: ['Materias primas: selección, evaluación y control de calidad con datos por país', 'El pH como parámetro de seguridad: por qué 4,6 es la línea que nunca se puede cruzar', 'Grados Brix y actividad de agua: cómo medir si un producto es seguro', 'Métodos universales de conservación: calor, frío, sal, azúcar y acidez', 'El Clostridium botulinum explicado para el aula: qué es, por qué mata y cómo controlarlo'],
  result: 'Tus alumnos aprenden a determinar si un alimento procesado es seguro, usando parámetros técnicos reales y medibles en el taller.'
}, {
  icon: '📙',
  number: 'Tomo 3',
  title: 'Tecnología de Lácteos y Cárnicos',
  subtitle: 'Cómo enseñar quesos, embutidos y curados con formulaciones exactas y parámetros técnicos correctos.',
  problem: 'Los lácteos y los cárnicos son los productos de mayor complejidad técnica y mayor valor agregado en la industria alimentaria artesanal. Este tomo te da el material para explicarlos de forma ordenada, segura y profundamente contextualizada en la realidad productiva de América Latina.',
  bullets: ['Tecnología de lácteos: quesos frescos, madurados, yogur, mantequilla y dulce de leche regional', 'Los quesos más importantes de cada país con sus procesos técnicos completos', 'Tecnología de cárnicos: embutidos frescos, cocidos, secos y fermentados', 'Formulaciones completas con porcentajes exactos: chorizo, longaniza, mozzarella, salame', 'Ingredientes funcionales: sal, nitritos, fosfatos — qué hacen y por qué no son opcionales', 'La temperatura interna de cocción: el parámetro de seguridad que no se puede improvisar'],
  result: 'Tus alumnos aprenden a procesar lácteos y cárnicos con estándares técnicos reales, con datos específicos de su país y su región.'
}, {
  icon: '📕',
  number: 'Tomo 4',
  title: 'Del Envase al Consumidor',
  subtitle: 'El módulo más práctico y comercial: conservas avanzadas, envasado, rotulado y acceso al mercado.',
  problem: 'Este es el tomo que transforma el taller escolar en un taller productivo real. Aquí tus alumnos aprenderán que lo que producen tiene valor comercial concreto en el mercado de su zona, y aprenderán a comunicarlo correctamente con un empaque que refleje esa calidad.',
  bullets: ['Conservas vegetales avanzadas: mermeladas premium, encurtidos artesanales, chutneys y fermentados naturales', 'Más de 20 recetas técnicas con formulaciones exactas y datos reales por país', 'Envasado y rotulado: todos los requisitos legales por país de América Latina', 'El etiquetado que agrega valor: cómo comunicar calidad artesanal con identidad regional', 'Canales de comercialización: ferias, restaurantes, tiendas naturistas, venta directa', 'Precios de referencia reales del mercado por producto y por país'],
  result: 'Tus alumnos comprenden que el producto que elaboran en el taller puede tener valor comercial real y aprenden a presentarlo correctamente para el mercado de su zona.',
  popular: true
}, {
  icon: '📓',
  number: 'Tomo 5',
  title: 'Del Taller al Emprendimiento',
  subtitle: 'El cierre que convierte el conocimiento técnico en un proyecto productivo real y viable.',
  problem: 'Muchos alumnos terminan el año sin saber cómo convertir lo que aprendieron en el taller en algo concreto y rentable. Este tomo les enseña a cerrar el proceso con un proyecto productivo bien calculado, fundamentado y presentable.',
  bullets: ['Costos de producción: cómo calcularlos correctamente con precios reales de la zona', 'Precio de venta: la diferencia entre el precio de costo y el precio de valor percibido', 'Análisis económico de cada producto elaborado en el año', 'Acceso a mercados: normativas sanitarias por país para comenzar a vender legalmente', 'Proyecto Final Productivo 360°: el trabajo integrador que cierra el año con un producto real'],
  result: 'Tus alumnos cierran el año con un proyecto productivo real — con productos elaborados, calculados, envasados y listos para el mercado de su zona.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-black" data-id="element-279">
      <div className="max-w-6xl mx-auto" data-id="element-280">
        <div className="text-center mb-16" data-id="element-281">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-282">
            📘 Los 5 Tomos del Mega Pack
          </h2>
          <p className="text-xl text-gold-light" data-id="element-283">
            Material completo para el taller.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-284">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-dark-card rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-dark-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-285">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-286">
                  EL MÁS POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-287">
                <span className="text-4xl" data-id="element-288">{tomo.icon}</span>
                <div data-id="element-289">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-290">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1" data-id="element-291">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-292">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-293">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-294">
                  ¿Qué resuelve en el aula?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-295">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-296">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-297">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-298" />
                    <span className="text-gray-200 text-sm" data-id="element-299">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-300">
                <span className="text-gold font-bold block mb-1" data-id="element-301">
                  Resultado:
                </span>
                <p className="text-white text-sm" data-id="element-302">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}